import * as React from 'react';
import { Button, Link, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { isPaused } from '../../api/src/workspace';
import { useApp } from '../app/context';
import FolderExplorer from '../components/FolderExplorer';
import { getProgram } from '../../api/src/programs';
import { applicationView, type IApplicationView, type IClientView, itemView, localToday } from '../records/model';
import { daysBetween, eventView, type IEventView, openDues } from '../records/events';
import { useLoad } from '../records/useLoad';
import { needsYou, type INeed } from '../records/today';

function ago(d: Date | undefined): string {
  if (!d) {
    return '';
  }
  const m: number = Math.round((Date.now() - d.getTime()) / 60000);
  return m < 1 ? 'just now' : m === 1 ? '1 min ago' : m < 60 ? `${m} min ago` : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** "Oct 9" for a YYYY-MM-DD date, read as a local date. */
export function shortDate(iso: string): string {
  const d: Date = new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

/**
 * Today: "N things need you", each with one button; then what's coming up, the folders as they
 * are (the folder browser stays here), and a short getting-started list while it's useful.
 */
export default function Home(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const filing: boolean = !isPaused(w);
  const ready: boolean = !!w.firm?.listsReady;
  const { data, error } = useLoad(async () => {
    if (!ready) {
      return undefined;
    }
    const [apps, events, clients, uploaded] = await Promise.all([
      app.records.fromSets(set => set.applications.all()), app.records.fromSets(set => set.events.all()), app.records.allClients(),
      // Older lists may not have this column indexed until something is written: then skip it.
      app.records.fromSets(set => set.checklist.query({ Status: 'Uploaded by client (check)' })).catch(() => [])
    ]);
    return { apps: apps.map(applicationView), events: events.map(eventView), clients, uploaded: uploaded.map(itemView) };
  }, [app.records, ready]);
  const today: string = localToday();

  const names: { [key: string]: string } = {};
  for (const c of data?.clients ?? []) {
    names[c.key] = c.name;
  }
  const byKey: { [key: string]: IApplicationView } = {};
  for (const a of data?.apps ?? []) {
    byKey[a.key] = a;
  }
  const needs: INeed[] = data ? needsYou({ ...data, today, names }) : [];
  // Dates of open files; follow-up dates of any file, even closed. Unchecked deadlines are already above.
  const soon: IEventView[] = data
    ? openDues(data.events)
      .filter(e => (byKey[e.applicationKey]?.open || (e.kind === 'followup' && !!byKey[e.applicationKey])) && daysBetween(today, e.due!) <= 30 && e.due! >= today)
      .slice(0, 8)
    : [];
  const who = (key: string): string => {
    const a: IApplicationView | undefined = byKey[key];
    return a ? names[a.clientKey] ?? a.title : '';
  };
  const started = gettingStarted(app, data?.clients, data?.apps);

  return (
    <main className="page" style={{ maxWidth: 908 }}>
      <div className="stack" style={{ gap: 6 }}>
        <span className="dateline">{new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        <h1 className="h1">
          {!ready ? 'Finish setting up' : !data ? 'Today' : needs.length === 0 ? 'Nothing needs you today' : `${needs.length} thing${needs.length === 1 ? '' : 's'} need${needs.length === 1 ? 's' : ''} you today`}
        </h1>
      </div>
      {(error || app.inbox.error) && <MessageBar intent="error"><MessageBarBody>{error ?? app.inbox.error}</MessageBarBody></MessageBar>}

      {!ready && (
        <section className="needs">
          <div className="task">
            <span className="flag" aria-hidden="true" />
            <div className="stack"><strong>Create your firm's lists</strong><span className="small" style={{ fontSize: 14 }}>The last step of setup, before adding clients and applications.</span></div>
            <Button appearance="primary" as="a" href="#/setup">Finish setup</Button>
          </div>
        </section>
      )}
      {ready && !data && !error && <Spinner size="small" label="Looking at your files…" labelPosition="after" />}
      {needs.length > 0 && (
        <section className="needs" aria-label="Needs you">
          {needs.slice(0, 12).map((n, i) => (
            <div key={n.key} className="task">
              <span className={n.quiet ? 'flag quiet' : 'flag'} aria-hidden="true" />
              <div className="stack">
                <span className={n.quiet ? 'why quiet' : 'why'}>{n.why}</span>
                <strong>{n.title}</strong>
                <span className="small" style={{ fontSize: 14 }}>{n.detail}</span>
              </div>
              <Button appearance={i === 0 ? 'primary' : 'secondary'} as="a" href={n.href}>{n.action}</Button>
            </div>
          ))}
          {needs.length > 12 && <div className="task"><span className="small">and {needs.length - 12} more on the <a href="#/applications">Applications</a> board.</span></div>}
        </section>
      )}

      {ready && data && (
        <section aria-labelledby="soon-h" className="rows">
          <div className="spread" style={{ marginBottom: 6 }}>
            <h2 id="soon-h" className="h2">Coming up</h2>
            <a href="#/applications" style={{ fontSize: 14 }}>All applications</a>
          </div>
          {soon.length === 0 && <div className="row"><span className="muted">Nothing else in the next 30 days.</span></div>}
          {soon.map(e => (
            <a key={e.key} className="row" href={`#/applications/${encodeURIComponent(e.applicationKey)}/${e.kind === 'followup' ? 'closing' : 'deadlines'}`} style={{ color: 'inherit', textDecoration: 'none' }}>
              <span className="when">{shortDate(e.due!)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{e.title} <span className="muted">· {who(e.applicationKey)}{byKey[e.applicationKey] ? ` · ${getProgram(byKey[e.applicationKey].programId)?.short ?? ''}` : ''}</span></span>
              {e.kind === 'instalment' && <span className="status">to bill</span>}
              {e.kind === 'followup' && <span className="status">follow up</span>}
            </a>
          ))}
        </section>
      )}

      {filing && (
        <span className="small" role="status" style={{ fontSize: 14 }}>
          Inbox filing is on: {app.inbox.checking ? 'reading Inbox…' : `watching ${w.inboxName}${app.inbox.lastChecked ? ` · checked ${ago(app.inbox.lastChecked)}` : ''}`}
          {' · '}<Link as="button" disabled={app.inbox.checking} onClick={() => { void app.inbox.checkNow(); }}>Check now</Link>
        </span>
      )}

      {/* The library as it is right now: open folders, rename or move them (logged, undoable). */}
      <FolderExplorer title="Your folders" />

      {ready && data && started.done < started.steps.length && (
        <details style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <summary style={{ cursor: 'pointer', minHeight: 44, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: 15 }}>
            Getting started · {started.done} of {started.steps.length} done
          </summary>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: 0, padding: '8px 0 0 20px', fontSize: 14 }}>
            {started.steps.map(s => <li key={s.label}>{s.done ? <span className="muted">{s.label} · done</span> : <a href={s.href}>{s.label}</a>}</li>)}
          </ul>
        </details>
      )}
    </main>
  );
}

function gettingStarted(app: ReturnType<typeof useApp>, clients: IClientView[] | undefined, apps: IApplicationView[] | undefined): { done: number; steps: { label: string; href: string; done: boolean }[] } {
  const steps = [
    { label: 'Set up your firm', href: '#/settings', done: true },
    { label: 'Add your first client', href: '#/clients/new', done: (clients ?? []).length > 0 },
    { label: 'Open your first application', href: '#/applications/new', done: (apps ?? []).length > 0 },
    { label: 'Turn on client upload links', href: '#/settings', done: !!app.workspace.uploads?.enabled },
    { label: 'Set up restricted files', href: '#/settings', done: !!app.workspace.firm?.restricted }
  ];
  return { done: steps.filter(s => s.done).length, steps };
}
